import type {
  Allergen,
  HouseholdPersonDto,
  HouseholdRole,
  MealPlaceDto,
  MealPlanDto,
  MealSlot,
  MealTimeDto,
  PersonPreferenceDto,
  PersonProfileRequest,
  PlanWarningDto,
  PreferenceKind,
} from '@fewerbags/schema';

/**
 * Eating profiles, meal places and who-eats-what (D31/D32): query keys and
 * the pure helpers behind the profile sheet, the places page, the meal-time
 * extras and the plan page's attendee editor.
 */

export const profileKeys = {
  /** Under the `household` prefix with the people list. */
  places: ['household', 'mealPlaces'] as const,
  preferences: (personId: string) => ['household', 'people', personId, 'preferences'] as const,
};

export const NOTES_MAX = 400;
export const GUEST_NAME_MAX = 80;
export const GUESTS_MAX = 30;

// ------------------------------------------------------------------ chips --

/**
 * Toggle `key` in a chip list, keeping the vocabulary's own order so the
 * request (and the chips) read the same way every time.
 */
export function toggleChip<T extends string>(
  list: readonly T[],
  key: T,
  vocabulary: readonly T[],
): T[] {
  const next = new Set(list);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return vocabulary.filter((k) => next.has(k));
}

function sameSet<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

// ------------------------------------------------------- person profiles --

/** Owners and adults edit anyone's profile; a member only their own person. */
export function canEditPerson(
  role: HouseholdRole | null | undefined,
  person: Pick<HouseholdPersonDto, 'isMe'>,
): boolean {
  return role === 'owner' || role === 'adult' || person.isMe;
}

export interface PreferenceChip {
  ingredientId: string;
  name: string;
}

export interface ProfileDraft {
  allergens: Allergen[];
  diets: HouseholdPersonDto['diets'];
  notes: string;
  likes: PreferenceChip[];
  dislikes: PreferenceChip[];
}

const chipOf = (p: PersonPreferenceDto): PreferenceChip => ({
  ingredientId: p.ingredientId,
  name: p.ingredientName,
});

/** The editable copy of a person's profile: stated preferences only. */
export function draftFromProfile(
  person: Pick<HouseholdPersonDto, 'allergens' | 'diets' | 'notes'>,
  preferences: readonly PersonPreferenceDto[],
): ProfileDraft {
  const stated = preferences.filter((p) => p.source === 'stated');
  return {
    allergens: [...person.allergens],
    diets: [...person.diets],
    notes: person.notes ?? '',
    likes: stated.filter((p) => p.kind === 'like').map(chipOf),
    dislikes: stated.filter((p) => p.kind === 'dislike').map(chipOf),
  };
}

/** Learned entries the draft has not already stated ("We think …"), strongest first. */
export function learnedOf(
  preferences: readonly PersonPreferenceDto[],
  draft: Pick<ProfileDraft, 'likes' | 'dislikes'>,
): PersonPreferenceDto[] {
  const stated = new Set([...draft.likes, ...draft.dislikes].map((c) => c.ingredientId));
  return preferences
    .filter((p) => p.source === 'learned' && !stated.has(p.ingredientId))
    .sort((a, b) => b.strength - a.strength || a.ingredientName.localeCompare(b.ingredientName));
}

/** Add a stated like/dislike; an ingredient is never on both lists. */
export function addPreference(
  draft: ProfileDraft,
  kind: PreferenceKind,
  chip: PreferenceChip,
): ProfileDraft {
  const other = kind === 'like' ? 'dislikes' : 'likes';
  const own = kind === 'like' ? 'likes' : 'dislikes';
  const without = (xs: PreferenceChip[]) => xs.filter((c) => c.ingredientId !== chip.ingredientId);
  return {
    ...draft,
    [other]: without(draft[other]),
    [own]: [...without(draft[own]), chip],
  };
}

export function removePreference(
  draft: ProfileDraft,
  kind: PreferenceKind,
  ingredientId: string,
): ProfileDraft {
  const own = kind === 'like' ? 'likes' : 'dislikes';
  return { ...draft, [own]: draft[own].filter((c) => c.ingredientId !== ingredientId) };
}

/** Learned entries just confirmed on the server join the draft's stated lists. */
export function mergeConfirmed(
  draft: ProfileDraft,
  confirmed: readonly PersonPreferenceDto[],
): ProfileDraft {
  return confirmed.reduce((d, p) => addPreference(d, p.kind, chipOf(p)), draft);
}

/** PATCH body with only what changed, or null when nothing did. */
export function profilePatch(
  person: Pick<HouseholdPersonDto, 'allergens' | 'diets' | 'notes'>,
  draft: Pick<ProfileDraft, 'allergens' | 'diets' | 'notes'>,
): PersonProfileRequest | null {
  const out: PersonProfileRequest = {};
  if (!sameSet(person.allergens, draft.allergens)) out.allergens = [...draft.allergens];
  if (!sameSet(person.diets, draft.diets)) out.diets = [...draft.diets];
  const notes = draft.notes.trim();
  if (notes !== (person.notes ?? '').trim()) out.notes = notes === '' ? null : notes;
  return Object.keys(out).length ? out : null;
}

/** PUT body replacing the stated lists that changed, or null when neither did. */
export function preferencesPatch(
  preferences: readonly PersonPreferenceDto[],
  draft: Pick<ProfileDraft, 'likes' | 'dislikes'>,
): { likes?: string[]; dislikes?: string[] } | null {
  const statedIds = (kind: PreferenceKind) =>
    preferences.filter((p) => p.source === 'stated' && p.kind === kind).map((p) => p.ingredientId);
  const likes = draft.likes.map((c) => c.ingredientId);
  const dislikes = draft.dislikes.map((c) => c.ingredientId);
  const out: { likes?: string[]; dislikes?: string[] } = {};
  if (!sameSet(statedIds('like'), likes)) out.likes = likes;
  if (!sameSet(statedIds('dislike'), dislikes)) out.dislikes = dislikes;
  return Object.keys(out).length ? out : null;
}

/** How sure the learned guess is, in words (strength is in (0, 1]). */
export function strengthLabel(strength: number): 'weak' | 'fair' | 'strong' {
  if (strength >= 0.66) return 'strong';
  if (strength >= 0.33) return 'fair';
  return 'weak';
}

/** "Emma likes …" / "Emma dislikes …" line for a learned entry. */
export function learnedText(name: string, p: Pick<PersonPreferenceDto, 'kind' | 'ingredientName'>) {
  return `${name} ${p.kind === 'like' ? 'likes' : 'dislikes'} ${p.ingredientName}`;
}

// ------------------------------------------------------------ meal places --

/** Home first, then the household's own order, then by name. */
export function sortPlaces<T extends Pick<MealPlaceDto, 'kind' | 'sort' | 'name'>>(
  places: readonly T[],
): T[] {
  return [...places].sort(
    (a, b) =>
      Number(b.kind === 'home') - Number(a.kind === 'home') ||
      a.sort - b.sort ||
      a.name.localeCompare(b.name),
  );
}

export const PLACE_KIND_LABEL: Record<MealPlaceDto['kind'], string> = {
  home: 'Home',
  work: 'Work',
  school: 'School',
  other: 'Other',
};

/** Place kinds a new or existing place may take: home only for the home row. */
export function placeKindOptions(isHome: boolean): Array<{ value: string; label: string }> {
  const kinds: MealPlaceDto['kind'][] = isHome ? ['home'] : ['work', 'school', 'other'];
  return kinds.map((k) => ({ value: k, label: PLACE_KIND_LABEL[k] }));
}

// ------------------------------------------------ meal-time place & people --

export interface CellExtra {
  /** null = home. */
  placeId: string | null;
  /** Empty = everyone. */
  attendeePersonIds: string[];
}
export type CellExtras = Record<string, CellExtra>;

export const cellKey = (weekday: number, slot: MealSlot) => `${weekday}:${slot}`;

export function extrasFromRows(rows: readonly MealTimeDto[]): CellExtras {
  const out: CellExtras = {};
  for (const r of rows) {
    out[cellKey(r.weekday, r.slot)] = {
      placeId: r.placeId ?? null,
      attendeePersonIds: [...(r.attendeePersonIds ?? [])],
    };
  }
  return out;
}

/**
 * Attach each row's place and attendees for the whole-week PUT. Every row
 * says explicitly what it wants (home / everyone included), so clearing a
 * cell's extras is saved too.
 */
export function withExtras(rows: readonly MealTimeDto[], extras: CellExtras): MealTimeDto[] {
  return rows.map((r) => {
    const e = extras[cellKey(r.weekday, r.slot)];
    return {
      ...r,
      placeId: e?.placeId ?? null,
      attendeePersonIds: e ? [...e.attendeePersonIds] : [],
    };
  });
}

/** Short label for a cell's extras ("School · Emma, Liam"), or null for home + everyone. */
export function cellExtraSummary(
  extra: CellExtra | undefined,
  placeName: (id: string) => string | undefined,
  personName: (id: string) => string | undefined,
): string | null {
  if (!extra) return null;
  const parts: string[] = [];
  const place = extra.placeId ? placeName(extra.placeId) : undefined;
  if (place) parts.push(place);
  const names = extra.attendeePersonIds.map(personName).filter((n): n is string => !!n);
  if (names.length) parts.push(names.join(', '));
  return parts.length ? parts.join(' · ') : null;
}

// --------------------------------------------------------- plan attendees --

export interface AttendeesDraft {
  personIds: string[];
  guests: string[];
}

export function attendeesDraftOf(plan: Pick<MealPlanDto, 'attendees'>): AttendeesDraft {
  return {
    personIds: plan.attendees.flatMap((a) => (a.personId && !a.guest ? [a.personId] : [])),
    guests: plan.attendees.filter((a) => a.guest || !a.personId).map((a) => a.name),
  };
}

export function sameAttendees(a: AttendeesDraft, b: AttendeesDraft): boolean {
  return (
    sameSet(a.personIds, b.personIds) &&
    a.guests.length === b.guests.length &&
    a.guests.every((g, i) => g === b.guests[i])
  );
}

export function togglePerson(draft: AttendeesDraft, personId: string): AttendeesDraft {
  return {
    ...draft,
    personIds: draft.personIds.includes(personId)
      ? draft.personIds.filter((id) => id !== personId)
      : [...draft.personIds, personId],
  };
}

/** Add a guest by name (trimmed, capped); blank or over the limit is ignored. */
export function addGuest(draft: AttendeesDraft, name: string): AttendeesDraft {
  const n = name.trim().slice(0, GUEST_NAME_MAX);
  if (!n || draft.guests.length >= GUESTS_MAX) return draft;
  return { ...draft, guests: [...draft.guests, n] };
}

export function removeGuest(draft: AttendeesDraft, index: number): AttendeesDraft {
  return { ...draft, guests: draft.guests.filter((_, i) => i !== index) };
}

/** Headcount the attendees make (guests count as adults); 0 = everyone. */
export function attendeeCount(draft: AttendeesDraft): number {
  return draft.personIds.length + draft.guests.length;
}

// --------------------------------------------------------- plan warnings --

export type WarningTone = 'error' | 'warning' | 'info';

export const WARNING_TONE: Record<PlanWarningDto['kind'], WarningTone> = {
  allergen: 'error',
  place_allergen: 'error',
  diet: 'warning',
  dislike: 'info',
};

export const WARNING_LABEL: Record<PlanWarningDto['kind'], string> = {
  allergen: 'Allergy',
  place_allergen: 'Not allowed there',
  diet: 'Diet',
  dislike: 'Dislike',
};

const TONE_RANK: Record<WarningTone, number> = { error: 0, warning: 1, info: 2 };

/** Most serious first; the original order otherwise. */
export function sortWarnings<T extends Pick<PlanWarningDto, 'kind'>>(warnings: readonly T[]): T[] {
  return warnings
    .map((w, i) => ({ w, i }))
    .sort(
      (a, b) => TONE_RANK[WARNING_TONE[a.w.kind]] - TONE_RANK[WARNING_TONE[b.w.kind]] || a.i - b.i,
    )
    .map((x) => x.w);
}

/** The most serious tone among the warnings, or null when there are none. */
export function worstTone(warnings: readonly Pick<PlanWarningDto, 'kind'>[]): WarningTone | null {
  const first = sortWarnings(warnings)[0];
  return first ? WARNING_TONE[first.kind] : null;
}
